import React, { useState, useRef, useEffect, useCallback } from "react";
import { X, Send, Trash2, Loader2, ShoppingBag, Gift, Search, Home, Sparkles, MessageCircle, Phone, Mail, HelpCircle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { API } from "../lib/api";
import AethelAuraIcon from "./AethelAuraIcon";

const SESSION_KEY = "aethelbee_session_id";
const FIRST_VISIT_KEY = "aethel_first_visit";

function getSessionId() {
  let sid = localStorage.getItem(SESSION_KEY);
  if (!sid) {
    sid = "aura-" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
    localStorage.setItem(SESSION_KEY, sid);
  }
  return sid;
}

function isFirstVisit() {
  if (!localStorage.getItem(FIRST_VISIT_KEY)) {
    localStorage.setItem(FIRST_VISIT_KEY, Date.now().toString());
    return true;
  }
  return false;
}

const CONVERSATION_STARTERS = [
  { icon: Home, label: "Decor for my home", msg: "I'm looking to decorate my home. Can you help me find the right pieces?" },
  { icon: Gift, label: "Gift ideas", msg: "I need a beautiful home decor gift. Can you suggest something special?" },
  { icon: Search, label: "Find a product", msg: "I'm looking for a specific product. Can you help me find it?" },
  { icon: ShoppingBag, label: "Shop bestsellers", msg: "Show me your best sellers and most popular items!" },
  { icon: Sparkles, label: "Dining table styling", msg: "I want to style my dining table beautifully. What do you recommend?" },
  { icon: HelpCircle, label: "Need help?", msg: "I have a question about my order or need customer support." },
];

function renderMessageContent(text) {
  const urlRegex = /(https?:\/\/[^\s,)]+)/g;
  const parts = text.split(urlRegex);
  return parts.map((part, i) =>
    urlRegex.test(part) ? (
      <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="text-[#1A3626] underline underline-offset-2 decoration-[#D4AF37]/60 hover:text-[#D4AF37] transition-colors break-all font-medium">{part}</a>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    )
  );
}

export default function AethelBeeChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [showBubble, setShowBubble] = useState(false);
  const [firstVisit] = useState(isFirstVisit);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const sessionId = useRef(getSessionId());

  // Show welcome bubble after delay
  useEffect(() => {
    if (open) return;
    const timer = setTimeout(() => setShowBubble(true), 2500);
    return () => clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, streaming]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  useEffect(() => {
    if (open) setShowBubble(false);
  }, [open]);

  const sendMessage = useCallback(async (text) => {
    if (!text.trim() || streaming) return;
    const userMsg = { role: "user", content: text.trim() };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setStreaming(true);
    setMessages((prev) => [...prev, { role: "assistant", content: "" }]);

    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text.trim(), session_id: sessionId.current }),
      });
      if (!res.ok) throw new Error("Chat request failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          try {
            const payload = JSON.parse(line.slice(6));
            if (payload.type === "delta") {
              setMessages((prev) => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                if (last?.role === "assistant") updated[updated.length - 1] = { ...last, content: last.content + payload.content };
                return updated;
              });
            } else if (payload.type === "error") {
              setMessages((prev) => {
                const updated = [...prev];
                updated[updated.length - 1] = { role: "assistant", content: payload.content };
                return updated;
              });
            }
          } catch {}
        }
      }
    } catch {
      setMessages((prev) => {
        const updated = [...prev];
        if (updated[updated.length - 1]?.role === "assistant" && !updated[updated.length - 1].content) {
          updated[updated.length - 1] = {
            role: "assistant",
            content: "I'm having a little trouble connecting right now. Please try again in a moment or reach out to our Aethel Decor team on WhatsApp at +91 9599 272 633.",
          };
        }
        return updated;
      });
    } finally {
      setStreaming(false);
    }
  }, [streaming]);

  const handleSubmit = (e) => { e.preventDefault(); sendMessage(input); };

  const clearChat = async () => {
    try { await fetch(`${API}/chat/${sessionId.current}`, { method: "DELETE" }); } catch {}
    setMessages([]);
    const newSid = "aura-" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
    localStorage.setItem(SESSION_KEY, newSid);
    sessionId.current = newSid;
  };

  const hasMessages = messages.length > 0;

  return (
    <>
      {/* ─── Floating Avatar + Welcome Bubble ─── */}
      <AnimatePresence>
        {!open && (
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 22 }}
            className="fixed bottom-5 right-5 z-50 flex flex-col items-end gap-2"
          >
            {/* Speech bubble */}
            <AnimatePresence>
              {showBubble && (
                <motion.div
                  initial={{ opacity: 0, y: 8, scale: 0.9 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 8, scale: 0.9 }}
                  transition={{ duration: 0.35, ease: "easeOut" }}
                  className="relative mr-2 max-w-[220px] px-4 py-3 rounded-2xl rounded-br-sm shadow-lg border border-[#D4AF37]/15 cursor-pointer"
                  style={{ background: "linear-gradient(135deg, #FFFDF7 0%, #F5F0E6 100%)" }}
                  onClick={() => setOpen(true)}
                  data-testid="aura-welcome-bubble"
                >
                  <p className="text-[13px] leading-relaxed text-[#1A3626]">
                    <span className="font-semibold">Hi! I'm AethelAura</span> <span style={{color:"#c7254e"}}>&#10084;</span>
                    <br />
                    <span className="text-[#7a7668]">Your D&eacute;cor Concierge</span> <span style={{color:"#D4AF37"}}>&#10024;</span>
                    <br />
                    <span className="text-[#7a7668] text-xs">How can I help you today?</span>
                  </p>
                  <button
                    onClick={(e) => { e.stopPropagation(); setShowBubble(false); }}
                    className="absolute -top-1.5 -left-1.5 w-5 h-5 bg-white rounded-full shadow flex items-center justify-center border border-border"
                    aria-label="Dismiss"
                  >
                    <X size={10} className="text-muted-foreground" />
                  </button>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Video avatar button — 9:16 portrait */}
            <motion.button
              whileHover={{ scale: 1.06 }}
              whileTap={{ scale: 0.94 }}
              onClick={() => setOpen(true)}
              data-testid="aethelbee-chat-toggle"
              className="relative flex items-center justify-center cursor-pointer overflow-hidden"
              style={{
                width: 60,
                height: 107,
                borderRadius: 18,
                boxShadow: "0 4px 24px rgba(26,54,38,0.28), 0 0 0 2px rgba(212,175,55,0.3)",
              }}
              aria-label="Open AethelAura chat assistant"
            >
              <AethelAuraIcon size="fab" animate expression="wave" />
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ─── Chat Panel ─── */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.95 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            data-testid="aethelbee-chat-panel"
            className="fixed bottom-4 right-4 z-50 w-[400px] max-w-[calc(100vw-2rem)] h-[580px] max-h-[calc(100vh-2rem)] flex flex-col rounded-2xl shadow-2xl overflow-hidden border border-[#D4AF37]/15"
            style={{ background: "#FAFAF7" }}
            role="dialog"
            aria-label="AethelAura chat"
          >
            {/* ── Header ── */}
            <div
              className="flex items-center gap-3 px-4 py-3 border-b"
              style={{ background: "linear-gradient(135deg, #1A3626 0%, #2a5a3f 100%)", borderColor: "#D4AF37" }}
            >
              <div className="w-10 h-12 rounded-lg overflow-hidden ring-2 ring-[#D4AF37]/40 flex-shrink-0">
                <AethelAuraIcon size="sm" animate expression={streaming ? "think" : "idle"} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-semibold leading-tight flex items-center gap-1.5">
                  AethelAura <span style={{color:"#D4AF37"}}>&#10024;</span>
                </p>
                <p className="text-white/60 text-xs leading-tight">Your D&eacute;cor Concierge</p>
              </div>
              <button
                onClick={clearChat}
                data-testid="aethelbee-clear-chat"
                className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
                title="Clear chat"
                aria-label="Clear chat history"
              >
                <Trash2 className="w-4 h-4 text-white/50" />
              </button>
              <button
                onClick={() => setOpen(false)}
                data-testid="aethelbee-chat-close"
                className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
                aria-label="Close chat"
              >
                <X className="w-4 h-4 text-white/70" />
              </button>
            </div>

            {/* ── Messages ── */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3" data-testid="aethelbee-chat-messages">
              {!hasMessages && (
                <div className="pt-2">
                  {/* Welcome mascot */}
                  <motion.div
                    initial={{ scale: 0.85, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: "spring", stiffness: 200, damping: 16 }}
                    className="flex justify-center mb-3"
                  >
                    <div className="overflow-hidden" style={{ width: 80, height: 142, borderRadius: 14, boxShadow: "0 2px 12px rgba(26,54,38,0.12)" }}>
                      <AethelAuraIcon size="xl" animate expression="wave" />
                    </div>
                  </motion.div>

                  {/* Welcome text */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15 }}
                    className="text-center mb-4"
                  >
                    <p className="font-display text-lg text-[#1A3626]">
                      {firstVisit ? "Welcome to Aethel Decor!" : "Hi! I'm AethelAura"} <span style={{color:"#c7254e"}}>&#10084;</span>
                    </p>
                    <p className="text-sm text-[#7a7668] mt-1 leading-relaxed max-w-[280px] mx-auto">
                      {firstVisit
                        ? "I'm AethelAura, your personal D\u00e9cor Concierge. Tell me what you're looking for, and I'll help you find something beautiful."
                        : "Your D\u00e9cor Concierge. I'll help you find the perfect pieces for your space."
                      }
                    </p>
                  </motion.div>

                  {/* Conversation starters */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="grid grid-cols-2 gap-2 px-1"
                  >
                    {CONVERSATION_STARTERS.map((cs) => (
                      <button
                        key={cs.label}
                        onClick={() => sendMessage(cs.msg)}
                        data-testid={`aura-starter-${cs.label.toLowerCase().replace(/\s+/g, "-")}`}
                        className="flex items-center gap-2 px-3 py-2.5 text-left text-xs border border-[#e5e2d9] rounded-xl hover:border-[#1A3626]/40 hover:bg-[#1A3626]/[0.03] transition-all duration-200 group"
                      >
                        <cs.icon size={14} className="text-[#D4AF37] flex-shrink-0 group-hover:text-[#1A3626] transition-colors" />
                        <span className="text-[#1A3626] leading-tight">{cs.label}</span>
                      </button>
                    ))}
                  </motion.div>

                  {/* Human handoff */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                    className="flex items-center justify-center gap-3 mt-4 pt-3 border-t border-[#e5e2d9]"
                  >
                    <a href="https://wa.me/919599272633" target="_blank" rel="noreferrer" className="flex items-center gap-1 text-[10px] text-[#7a7668] hover:text-[#1A3626] transition-colors">
                      <Phone size={10} /> WhatsApp
                    </a>
                    <span className="text-[#e5e2d9]">|</span>
                    <a href="mailto:info@aetheldecor.com" className="flex items-center gap-1 text-[10px] text-[#7a7668] hover:text-[#1A3626] transition-colors">
                      <Mail size={10} /> Email Us
                    </a>
                    <span className="text-[#e5e2d9]">|</span>
                    <button onClick={() => sendMessage("I'd like to talk to your team for personalised help.")} className="flex items-center gap-1 text-[10px] text-[#7a7668] hover:text-[#1A3626] transition-colors">
                      <MessageCircle size={10} /> Talk to Team
                    </button>
                  </motion.div>
                </div>
              )}

              {/* Chat messages */}
              {messages.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                  {msg.role === "assistant" && (
                    <div className="w-7 h-12 flex-shrink-0 mr-2 mt-0.5 rounded-md overflow-hidden">
                      <AethelAuraIcon
                        size="sm"
                        animate={!msg.content && streaming}
                        expression={!msg.content && streaming ? "think" : "idle"}
                      />
                    </div>
                  )}
                  <div
                    className={`max-w-[80%] px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                      msg.role === "user"
                        ? "bg-[#1A3626] text-white rounded-2xl rounded-br-md"
                        : "bg-white text-[#1a1a1a] rounded-2xl rounded-bl-md shadow-sm border border-[#e5e2d9]"
                    }`}
                  >
                    {msg.role === "assistant" ? renderMessageContent(msg.content) : msg.content}
                    {msg.role === "assistant" && !msg.content && streaming && (
                      <span className="inline-flex gap-1 items-center">
                        <span className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                        <span className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                        <span className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                      </span>
                    )}
                  </div>
                </div>
              ))}

              {/* Post-message quick helpers */}
              {hasMessages && !streaming && (
                <div className="flex justify-center pt-1 gap-2 flex-wrap">
                  <button onClick={() => sendMessage("Show me your best sellers")} className="text-[10px] px-2.5 py-1 rounded-full border border-[#e5e2d9] text-[#7a7668] hover:border-[#1A3626]/30 hover:text-[#1A3626] transition-colors">
                    Best sellers
                  </button>
                  <button onClick={() => sendMessage("I'd like to talk to your team")} className="text-[10px] px-2.5 py-1 rounded-full border border-[#e5e2d9] text-[#7a7668] hover:border-[#1A3626]/30 hover:text-[#1A3626] transition-colors">
                    Talk to team
                  </button>
                  <button onClick={() => sendMessage("What's your return policy?")} className="text-[10px] px-2.5 py-1 rounded-full border border-[#e5e2d9] text-[#7a7668] hover:border-[#1A3626]/30 hover:text-[#1A3626] transition-colors">
                    Returns info
                  </button>
                </div>
              )}
            </div>

            {/* ── Input ── */}
            <form onSubmit={handleSubmit} className="px-3 py-3 border-t border-[#e5e2d9] bg-white">
              <div className="flex items-center gap-2">
                <input
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask AethelAura anything..."
                  disabled={streaming}
                  data-testid="aethelbee-chat-input"
                  className="flex-1 text-sm px-3.5 py-2.5 rounded-xl border border-[#e5e2d9] bg-[#FAFAF7] focus:outline-none focus:border-[#1A3626]/40 placeholder:text-[#b5b0a3] disabled:opacity-50"
                  aria-label="Type your message"
                />
                <button
                  type="submit"
                  disabled={!input.trim() || streaming}
                  data-testid="aethelbee-chat-send"
                  className="w-10 h-10 rounded-xl flex items-center justify-center transition-all disabled:opacity-30"
                  style={{ background: input.trim() && !streaming ? "#1A3626" : "#e5e2d9" }}
                  aria-label="Send message"
                >
                  {streaming ? (
                    <Loader2 className="w-4 h-4 text-white animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 text-white" />
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
